import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  CampaignSettingsProps,
  GetCampaignResponse,
  CampaignErrorResponse,
} from '@dreamai/types';
import api from '../../../../api';
import ErrorState from '../../../common/ErrorState';
import { useDependencyUpdates } from '../../../../providers';

import './campaign-settings.scss';

interface CampaignSettingsComponentProps extends CampaignSettingsProps {
  threadId?: string;
  updateThreadViewVisualDescription?: (
    visualDescription: string,
  ) => Promise<void>;
  isInboxView?: boolean;
  onInboxViewComponentViewClick?: (componentId: string) => void;
}

const CampaignSettings: React.FC<CampaignSettingsComponentProps> = ({
  campaignId,
  threadId,
  updateThreadViewVisualDescription,
  isInboxView,
  onInboxViewComponentViewClick,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [campaign, setCampaign] = useState<
    GetCampaignResponse['campaign'] | null
  >(null);

  useEffect(() => {
    const fetchCampaign = async () => {
      try {
        setLoading(true);
        setError(null);

        const token = await getAccessTokenSilently();
        api.defaults.headers.common['Authorization'] = `Bearer ${token}`;

        const response = await api.get<
          GetCampaignResponse | CampaignErrorResponse
        >(`/campaigns/${campaignId}`);

        if (response.data.success) {
          setCampaign(response.data.campaign);
        } else {
          setError(response.data.error);
        }
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Failed to fetch campaign',
        );
      } finally {
        setLoading(false);
      }
    };

    if (campaignId) {
      fetchCampaign();
    }
  }, [campaignId, getAccessTokenSilently]);

  // Listen for campaign updates
  const { registerCallback } = useDependencyUpdates();
  useEffect(() => {
    const unregister = registerCallback('campaigns', campaignId, (update) => {
      console.log('Campaign updated in settings, reloading...');
      const fetchCampaign = async () => {
        try {
          const token = await getAccessTokenSilently();
          api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          const response = await api.get<
            GetCampaignResponse | CampaignErrorResponse
          >(`/campaigns/${campaignId}`);
          if (response.data.success) {
            setCampaign(response.data.campaign);
          }
        } catch (err) {
          console.error('Failed to refresh campaign:', err);
        }
      };
      fetchCampaign();
    });

    return () => unregister();
  }, [campaignId, registerCallback, getAccessTokenSilently]);

  if (loading || (!campaign && !error)) {
    return (
      <div className='campaign-settings-loading'>
        <div className='loading-skeleton'></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='campaign-settings-error'>
        <ErrorState
          title="I can't load your campaign settings right now"
          subtitle='You can try again, or refresh the page.'
        />
      </div>
    );
  }

  if (!campaign) {
    return <div></div>;
  }

  return (
    <div className='campaign-settings'>
      <div className='campaign-settings-header'>
        <div className='top-header'>
          <h2>Campaign settings</h2>
        </div>
        <p className='campaign-settings-subtitle'>
          Ask your Dreamie if you want to change campaign settings.
        </p>
      </div>

      <div className='campaign-settings-section'>
        <h3>Campaign goal</h3>
        <div className='campaign-settings-box campaign-goal-box'>
          {campaign.useCaseDescription || 'No campaign goal set'}
        </div>
      </div>

      <div className='campaign-settings-section'>
        <h3>Reply approvals</h3>
        <div className='campaign-settings-box'>
          {campaign.responsesSupervised
            ? 'Your Sales Pro will ask for approval before replying to customer questions.'
            : "Your Sales Pro will automatically reply to customer questions, unless it can't answer them."}
        </div>
      </div>

      <div className='campaign-settings-section'>
        <h3>Open tracking</h3>
        <div className='campaign-settings-box'>
          {campaign.emailOpenTracking
            ? "You'll be able to track when people have opened or viewed your email."
            : 'Open tracking is disabled for this campaign.'}
        </div>
      </div>
    </div>
  );
};

export default CampaignSettings;
